// The Hand to AE dialog and its toast with Undo (spec C6), for any page that offers it: the row menu,
// the Quick view and the Record. Undo cancels the handoff while it still waits.
import { useState } from 'react';
import type { Api, HandoffReason } from '../auth/api';
import { Toast } from '../shell/Toast';
import { HandToAeDialog, type HandRow } from './HandToAe';

/** `onChanged` reads the page again after a hand off or an Undo. */
export function useHandToAe(api: Api, onChanged: () => void) {
  const [row, setRow] = useState<HandRow | null>(null);
  const [preset, setPreset] = useState<{ reason: HandoffReason; note: string } | null>(null);
  const [toast, setToast] = useState<{ id: number; text: string; undo: string | null } | null>(
    null,
  );

  const undo = async (handoffId: string) => {
    const res = await api.cancelHandoff(handoffId);
    setToast({
      id: Date.now(),
      text: res.ok
        ? 'Undone. Back where it was.'
        : 'Could not undo. The AE may have answered it already.',
      undo: null,
    });
    onChanged();
  };

  const element = (
    <>
      {row && (
        <HandToAeDialog
          api={api}
          row={row}
          preset={preset}
          onClose={() => setRow(null)}
          onDone={(text, handoffId) => {
            setRow(null);
            setToast({ id: Date.now(), text, undo: handoffId });
            onChanged();
          }}
        />
      )}
      {toast && (
        <Toast
          key={toast.id}
          text={toast.text}
          action={toast.undo ? { label: 'Undo', onClick: () => void undo(toast.undo!) } : undefined}
          shownMs={toast.undo ? 8000 : undefined}
        />
      )}
    </>
  );

  return {
    /** `preset`: a one-click hand's reason and note (the Record board), else an empty dialog. */
    open: (r: HandRow, preset: { reason: HandoffReason; note: string } | null = null) => {
      setPreset(preset);
      setRow(r);
    },
    element,
  };
}
